import type { RefObject } from 'react';
import { useIntl } from 'react-intl';
import { usePopupState, bindTrigger, bindPopover } from 'material-ui-popup-state/hooks';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Tooltip from 'components/mui/Tooltip';
import CountChip from 'components/AgGrid/components/TagsCellRenderer/CountChip';

import { OverflowFilterChip } from 'pages/company/WorkspaceRoute/components/WorkspaceSearch/StyledComponents';
import type { RenderChip } from 'pages/company/WorkspaceRoute/components/WorkspaceSearch/types';

interface OverflowChipsProps {
  hiddenChips: RenderChip[];
  // The surface anchors to the whole input, not to the "+N" chip: the counter sits wherever the chips
  // happen to end, so a left-aligned surface hung off it drifted across the header and could run past
  // the viewport edge.
  anchorRef: RefObject<HTMLElement>;
  // Measured `clientWidth` of the input shell — the paper's width, not a cap.
  surfaceWidth: number;
}

// The "+N" overflow control: a CountChip that opens a Popover listing the hidden
// chips, each individually removable (req: chip overflow collapses into a clickable
// count control). Renders nothing when all chips fit.
const OverflowChips = ({ hiddenChips, anchorRef, surfaceWidth }: OverflowChipsProps) => {
  const { formatMessage } = useIntl();
  const popupState = usePopupState({ variant: 'popover', popupId: 'workspaceSearchOverflow' });

  if (hiddenChips.length === 0) return null;

  return (
    <>
      <CountChip
        size="small"
        clickable
        label={hiddenChips.length}
        aria-label={formatMessage(
          { id: 'company.workspace.search.overflowCount', defaultMessage: '{count} more filters' },
          { count: hiddenChips.length },
        )}
        {...bindTrigger(popupState)}
      />
      <Popover
        {...bindPopover(popupState)}
        // Overrides the anchor `bindPopover` derives from the trigger (the "+N" chip). A FUNCTION
        // rather than `anchorRef.current`: a ref read during render is null on the first pass, while
        // MUI resolves the function when it positions the popover, i.e. after mount.
        anchorEl={() => anchorRef.current}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        // Full input width, exactly as Autocomplete sizes its suggestions dropdown
        // (`width: anchorEl.clientWidth`) — the two surfaces belong to the same input and should not
        // read as different kinds of thing. `|| undefined`: the width arrives from a ResizeObserver
        // after the first paint, and a paper forced to 0 would be invisible rather than mis-sized.
        // MUI's own `maxWidth: calc(100% - 32px)` on the paper stays as the viewport clamp.
        slotProps={{ paper: { sx: { width: surfaceWidth || undefined } } }}
      >
        <Stack direction="row" flexWrap="wrap" gap={0.5} p={1}>
          {hiddenChips.map((chip) => (
            <Tooltip key={chip.id} t={chip.title}>
              <OverflowFilterChip
                size="small"
                color="primary"
                variant="outlined"
                avatar={chip.avatar}
                icon={chip.icon}
                label={chip.label}
                onDelete={chip.onDelete}
              />
            </Tooltip>
          ))}
        </Stack>
      </Popover>
    </>
  );
};

export default OverflowChips;
